import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import ConfirmModal from "../components/ConfirmModal";
import PageGuideCard from "../components/PageGuideCard";
import { useAuth } from "../context/AuthContext";
import { appendDismissedGuide, PAGE_GUIDE_KEYS } from "../lib/pageGuides";
import { toast } from "../lib/toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MEALS = ["breakfast", "lunch", "dinner", "snack"];
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function toAbsoluteUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

function getRecipeImageUrls(recipe) {
  return (recipe?.images || [])
    .filter((media) => media?.image)
    .map((media) => toAbsoluteUrl(media.image));
}

function getRecipeThumbnail(recipe) {
  const imageUrls = getRecipeImageUrls(recipe);
  return imageUrls[0] || "";
}

function getBrowseRotationSeed(recipe) {
  const source = `${recipe?.id ?? ""}-${recipe?.title ?? ""}`;
  let hash = 0;

  for (let i = 0; i < source.length; i += 1) {
    hash = (hash * 31 + source.charCodeAt(i)) >>> 0;
  }

  return hash;
}

function getBrowseRotationConfig(recipe) {
  const seed = getBrowseRotationSeed(recipe);

  return {
    periodMs: 7000 + (seed % 4) * 400,
    offsetMs: (seed % 7) * 900,
  };
}

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - ((day + 6) % 7);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatDateRange(monday) {
  const sun = new Date(monday);
  sun.setDate(sun.getDate() + 6);
  const opts = { month: "short", day: "numeric" };
  const monStr = monday.toLocaleDateString("en-US", opts);
  const sunStr = sun.toLocaleDateString("en-US", opts);
  return `${monStr} - ${sunStr}, ${monday.getFullYear()}`;
}

function toISODate(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDayDate(monday, dayIndex) {
  const d = new Date(monday);
  d.setDate(d.getDate() + dayIndex);
  return d.getDate();
}

export default function MealPlannerPage() {
  const navigate = useNavigate();
  const { currentUser, updateCurrentUser } = useAuth();
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  // Recipe picker
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState(null); // { day, meal }
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pickerRanked, setPickerRanked] = useState([]);
  const [pickerRankedLoading, setPickerRankedLoading] = useState(false);
  const searchTimerRef = useRef(null);


  const [dragOverSlot, setDragOverSlot] = useState(null); // "day-meal"
  const [draggingEntry, setDraggingEntry] = useState(false); // true when dragging an existing entry
  const [dragOverTrash, setDragOverTrash] = useState(false);
  const browseDraggedRef = useRef(false); // tracks if a browse card was dragged (to prevent click)


  const [browseRecipes, setBrowseRecipes] = useState([]);
  const [browsePage, setBrowsePage] = useState(1);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseHasNext, setBrowseHasNext] = useState(false);
  const [browseHasPrev, setBrowseHasPrev] = useState(false);
  const [browseTotal, setBrowseTotal] = useState(0);


  const [browseMyRecipes, setBrowseMyRecipes] = useState(false);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterDiet, setFilterDiet] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [filterOptions, setFilterOptions] = useState({ categories: [], diets: [], popular_tags: [] });
  const [browseImageClock, setBrowseImageClock] = useState(() => Date.now());

  // Add to cart
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const [cartResult, setCartResult] = useState(null);

  // Clear/copy week
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);

  const handleDismissOnboarding = useCallback(async (guideKey) => {
    try {
      await updateCurrentUser({
        onboarding_dismissed_pages: appendDismissedGuide(currentUser, guideKey),
      });
    } catch (err) {
      console.error("Failed to dismiss onboarding:", err);
    }
  }, [currentUser, updateCurrentUser]);

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/meal-plans/", {
        params: { week_start: toISODate(weekStart) },
      });
      setPlan(res.data);
    } catch (err) {
      console.error("Failed to fetch meal plan:", err);
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  // Fetch filter options once
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const res = await axiosInstance.get("/api/recipes/filters/", {
          params: { visibility: "discover" },
        });
        setFilterOptions(res.data);
      } catch (err) {
        console.error("Failed to fetch filters:", err);
      }
    };
    fetchFilters();
  }, []);

  // Browse recipes with filters
  const fetchBrowseRecipes = useCallback(async () => {
    setBrowseLoading(true);
    try {
      const params = { visibility: browseMyRecipes ? "owned" : "discover", page: browsePage };
      if (filterCategory) params.category = filterCategory;
      if (filterDiet) params.diet_preference = filterDiet;
      if (filterTag) params.tag = filterTag;
      const res = await axiosInstance.get("/api/recipes/", { params });
      setBrowseRecipes(res.data.results || []);
      setBrowseHasNext(!!res.data.next);
      setBrowseHasPrev(!!res.data.previous);
      setBrowseTotal(res.data.count || 0);
    } catch (err) {
      console.error("Failed to fetch recipes:", err);
    } finally {
      setBrowseLoading(false);
    }
  }, [browsePage, browseMyRecipes, filterCategory, filterDiet, filterTag]);

  useEffect(() => {
    fetchBrowseRecipes();
  }, [fetchBrowseRecipes]);

  useEffect(() => {
    const hasRotatingBrowseCards = browseRecipes.some(
      (recipe) => getRecipeImageUrls(recipe).length > 1
    );
    if (!hasRotatingBrowseCards) return undefined;

    const intervalId = window.setInterval(() => {
      setBrowseImageClock(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [browseRecipes]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setBrowsePage(1);
  }, [browseMyRecipes, filterCategory, filterDiet, filterTag]);

  // Week navigation
  const shiftWeek = (direction) => {
    setWeekStart((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + direction * 7);
      return next;
    });
  };

  // Get entries for a specific day/meal slot
  const getEntries = (day, meal) => {
    if (!plan) return [];
    return plan.entries.filter((e) => e.day === day && e.meal === meal);
  };

  // --- Recipe Picker ---
  const openPicker = async (day, meal) => {
    setPickerTarget({ day, meal });
    setPickerOpen(true);
    setSearchQuery("");
    setSearchResults([]);
    setPickerRankedLoading(true);
    try {
      const res = await axiosInstance.get("/api/recipes/", {
        params: {
          visibility: "planner",
          ordering: "trending",
          page_size: 20,
        },
      });
      setPickerRanked(res.data.results || []);
    } catch (err) {
      console.error("Failed to fetch ranked recipes:", err);
      setPickerRanked([]);
    } finally {
      setPickerRankedLoading(false);
    }
  };

  const closePicker = () => {
    setPickerOpen(false);
    setPickerTarget(null);
    setSearchQuery("");
    setSearchResults([]);
    setPickerRanked([]);
  };

  useEffect(() => {
    if (!pickerOpen) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axiosInstance.get("/api/recipes/", {
          params: { search: searchQuery, visibility: "planner", page_size: 10 },
        });
        setSearchResults(res.data.results || []);
      } catch (err) {
        console.error("Search failed:", err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery, pickerOpen]);

  const addEntry = async (recipeId, day, meal) => {
    try {
      const res = await axiosInstance.post("/api/meal-plans/entries/", {
        recipe_id: recipeId,
        day,
        meal,
        week_start: toISODate(weekStart),
      });
      setPlan((prev) => ({
        ...prev,
        entries: [...prev.entries, res.data],
      }));
      closePicker();
      toast.success("Meal added to plan.");
    } catch (err) {
      console.error("Failed to add entry:", err);
      toast.error("Failed to add meal plan entry.");
    }
  };

  const removeEntry = async (entryId) => {
    try {
      await axiosInstance.delete(`/api/meal-plans/entries/${entryId}/remove/`);
      setPlan((prev) => ({
        ...prev,
        entries: prev.entries.filter((e) => e.id !== entryId),
      }));
      toast.success("Meal removed from plan.");
    } catch (err) {
      console.error("Failed to remove entry:", err);
      toast.error("Failed to remove meal plan entry.");
    }
  };

  // --- Drag and Drop ---
  const handleDragStart = (e, entry) => {
    e.dataTransfer.setData("application/json", JSON.stringify({ entryId: entry.id }));
    e.dataTransfer.effectAllowed = "move";
    setDraggingEntry(true);
  };

  const handleDragEnd = () => {
    setDraggingEntry(false);
    setDragOverTrash(false);
  };

  const handleBrowseDragStart = (e, recipe) => {
    e.dataTransfer.setData("application/json", JSON.stringify({ recipeId: recipe.id }));
    e.dataTransfer.effectAllowed = "copyMove";
    browseDraggedRef.current = true;
  };

  const handleBrowseClick = (recipeId) => {
    if (browseDraggedRef.current) {
      browseDraggedRef.current = false;
      return;
    }
    navigate(`/recipes/${recipeId}`);
  };

  const handleDragOver = (e, day, meal) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverSlot(`${day}-${meal}`);
  };

  const handleDragLeave = () => {
    setDragOverSlot(null);
  };

  const handleDrop = async (e, day, meal) => {
    e.preventDefault();
    setDragOverSlot(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData("application/json"));
      if (data.entryId) {
        // Move existing entry
        const res = await axiosInstance.patch(`/api/meal-plans/entries/${data.entryId}/`, {
          day,
          meal,
        });
        setPlan((prev) => ({
          ...prev,
          entries: prev.entries.map((e) => (e.id === data.entryId ? res.data : e)),
        }));
      } else if (data.recipeId) {
        // Add new entry from browse grid
        const res = await axiosInstance.post("/api/meal-plans/entries/", {
          recipe_id: data.recipeId,
          day,
          meal,
          week_start: toISODate(weekStart),
        });
        setPlan((prev) => ({
          ...prev,
          entries: [...prev.entries, res.data],
        }));
      }
      toast.success("Meal plan updated.");
    } catch (err) {
      console.error("Failed to drop entry:", err);
      toast.error("Failed to update meal plan.");
    }
  };

  // --- Trash drop zone ---
  const handleTrashDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverTrash(true);
  };

  const handleTrashDrop = async (e) => {
    e.preventDefault();
    setDragOverTrash(false);
    setDraggingEntry(false);
    try {
      const data = JSON.parse(e.dataTransfer.getData("application/json"));
      if (data.entryId) {
        await removeEntry(data.entryId);
      }
    } catch (err) {
      console.error("Failed to remove entry:", err);
    }
  };

  // --- Sync to Cart ---
  const syncToCart = async () => {
    if (!plan || plan.entries.length === 0) return;
    setAddToCartLoading(true);
    try {
      const res = await axiosInstance.post("/api/meal-plans/sync-cart/", {
        week_start: toISODate(weekStart),
      });
      window.dispatchEvent(new Event("cart-changed"));
      setCartResult(res.data);
      toast.success("Planned recipes added to cart.");
      fetchPlan(); // refresh to get updated checked_out_at values
    } catch (err) {
      console.error("Failed to sync cart:", err);
      toast.error("Failed to add planned recipes to cart.");
    } finally {
      setAddToCartLoading(false);
    }
  };

  // --- Clear week ---
  const clearWeek = async () => {
    try {
      await axiosInstance.delete(`/api/meal-plans/clear/?week_start=${toISODate(weekStart)}`);
      setPlan((prev) => ({ ...prev, entries: [] }));
      setShowClearConfirm(false);
      toast.success("Week cleared.");
    } catch (err) {
      console.error("Failed to clear week:", err);
      toast.error("Failed to clear week.");
    }
  };

  // --- Copy week to next ---
  const copyWeekToNext = async () => {
    setCopyLoading(true);
    const nextMonday = new Date(weekStart);
    nextMonday.setDate(nextMonday.getDate() + 7);
    try {
      await axiosInstance.post("/api/meal-plans/copy/", {
        from_week: toISODate(weekStart),
        to_week: toISODate(nextMonday),
      });
      // Navigate to the copied week
      setWeekStart(nextMonday);
      toast.success("Week copied.");
    } catch (err) {
      console.error("Failed to copy week:", err);
      toast.error("Failed to copy week.");
    } finally {
      setCopyLoading(false);
    }
  };

  // --- Week summary ---
  const weekSummary = useMemo(() => {
    if (!plan || !plan.entries.length) return null;
    const uniqueRecipes = new Map();
    for (const entry of plan.entries) {
      if (!uniqueRecipes.has(entry.recipe.id)) {
        uniqueRecipes.set(entry.recipe.id, entry.recipe);
      }
    }
    let totalPrep = 0;
    let totalCook = 0;
    for (const recipe of uniqueRecipes.values()) {
      totalPrep += recipe.prep_time || 0;
      totalCook += recipe.cook_time || 0;
    }
    const totalTime = totalPrep + totalCook;
    const hours = Math.floor(totalTime / 60);
    const mins = totalTime % 60;
    const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    return {
      recipes: plan.entries.length,
      uniqueRecipes: uniqueRecipes.size,
      totalTime: timeStr,
    };
  }, [plan]);

  // --- Render ---
  if (loading && !plan) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageGuideCard
        onboarding={currentUser}
        guideKey={PAGE_GUIDE_KEYS.MEAL_PLANNER}
        title="This is your meal planner"
        description="Use this page to map recipes onto the week, balance what you want to cook, and build a cleaner path from planning to cart."
        onDismiss={() => handleDismissOnboarding(PAGE_GUIDE_KEYS.MEAL_PLANNER)}
      />

      {/* Header row */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="type-page-title">Meal Planner</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowClearConfirm(true)}
            disabled={!plan?.entries?.length}
            className="text-sm text-red-500 hover:text-red-600 disabled:opacity-30 transition px-2 py-1"
          >
            Clear Week
          </button>
          <button
            onClick={copyWeekToNext}
            disabled={copyLoading || !plan?.entries?.length}
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-500 disabled:opacity-30 border border-gray-300 dark:border-gray-600 px-3 py-1.5 rounded transition"
          >
            {copyLoading ? "Copying..." : "Copy to Next Week"}
          </button>
          <button
            onClick={syncToCart}
            disabled={addToCartLoading || !plan?.entries?.length}
            className="bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white font-semibold px-4 py-1.5 rounded shadow transition text-sm"
          >
            {addToCartLoading ? "Syncing..." : "Sync to Cart"}
          </button>
        </div>
      </div>

      {/* Week summary */}
      {weekSummary && (
        <div className="type-muted mb-4 flex items-center gap-4">
          <span>{weekSummary.recipes} meal{weekSummary.recipes !== 1 ? "s" : ""} planned</span>
          <span className="text-gray-300 dark:text-gray-600">|</span>
          <span>{weekSummary.uniqueRecipes} unique recipe{weekSummary.uniqueRecipes !== 1 ? "s" : ""}</span>
          <span className="text-gray-300 dark:text-gray-600">|</span>
          <span>Est. {weekSummary.totalTime} total</span>
        </div>
      )}

      {/* Week navigation */}
      <div className="flex items-center justify-center gap-4 mb-6">
        <button
          onClick={() => shiftWeek(-1)}
          className="text-gray-500 dark:text-gray-400 hover:text-blue-500 text-xl font-bold px-2"
        >
          &lt;
        </button>
        <span className="type-section-title min-w-[240px] text-center">
          {formatDateRange(weekStart)}
        </span>
        <button
          onClick={() => shiftWeek(1)}
          className="text-gray-500 dark:text-gray-400 hover:text-blue-500 text-xl font-bold px-2"
        >
          &gt;
        </button>
        <button
          onClick={() => setWeekStart(getMonday(new Date()))}
          className="text-xs text-blue-500 hover:text-blue-600 border border-blue-300 dark:border-blue-600 px-2 py-1 rounded transition"
        >
          Today
        </button>
      </div>

      {/* Weekly grid — desktop: 7 cols, mobile: 1 col */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {DAY_NAMES.map((dayName, dayIdx) => (
          <div key={dayIdx} className="min-w-0">
            {/* Day header */}
            <div className="text-center py-2 bg-gray-100 dark:bg-gray-800 rounded-t-lg border-b border-gray-200 dark:border-gray-700">
              <span className="type-card-title">
                {dayName} {getDayDate(weekStart, dayIdx)}
              </span>
            </div>

            {/* Meal slots */}
            {MEALS.map((meal) => {
              const entries = getEntries(dayIdx, meal);
              const isOver = dragOverSlot === `${dayIdx}-${meal}`;
              return (
                <div
                  key={meal}
                  onDragOver={(e) => handleDragOver(e, dayIdx, meal)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, dayIdx, meal)}
                  className={`border border-gray-200 dark:border-gray-700 p-1.5 min-h-[80px] transition-colors ${
                    isOver ? "border-2 border-dashed border-blue-400 bg-blue-50 dark:bg-blue-900/20" : ""
                  }`}
                >
                  <p className="type-eyebrow mb-1">
                    {meal}
                  </p>

                  {entries.map((entry) => {
                    const thumbnailUrl = getRecipeThumbnail(entry.recipe);
                    return (
                    <div
                      key={entry.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, entry)}
                      onDragEnd={handleDragEnd}
                      className={`group rounded shadow-sm mb-1 p-1 cursor-grab active:cursor-grabbing hover:shadow-md transition ${
                        entry.checked_out_at
                          ? "bg-gray-50 dark:bg-gray-700/50 opacity-60"
                          : "bg-white dark:bg-gray-700"
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        {thumbnailUrl && (
                          <img
                            src={thumbnailUrl}
                            alt=""
                            draggable="false"
                            className="w-8 h-8 rounded object-cover shrink-0"
                          />
                        )}
                        <Link
                          to={`/recipes/${entry.recipe.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="type-body truncate flex-1 text-xs hover:text-blue-500 dark:hover:text-blue-400 transition"
                        >
                          {entry.checked_out_at && <span className="text-green-500 mr-0.5">&#10003;</span>}
                          {entry.recipe.title}
                          {entry.recipe.servings && (
                            <span className="type-muted ml-1 text-[10px]">({entry.recipe.servings})</span>
                          )}
                        </Link>
                        <button
                          onClick={() => removeEntry(entry.id)}
                          className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 text-xs shrink-0 transition-opacity"
                          title="Remove"
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                  )})}

                  {/* Add button */}
                  <button
                    onClick={() => openPicker(dayIdx, meal)}
                    className="w-full text-center text-gray-300 dark:text-gray-600 hover:text-blue-500 dark:hover:text-blue-400 text-lg leading-none transition"
                    title="Add recipe"
                  >
                    +
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Trash drop zone — visible only when dragging an existing entry */}
      {draggingEntry && (
        <div
          onDragOver={handleTrashDragOver}
          onDragLeave={() => setDragOverTrash(false)}
          onDrop={handleTrashDrop}
          className={`mt-4 flex items-center justify-center gap-2 py-4 rounded-lg border-2 border-dashed transition-colors ${
            dragOverTrash
              ? "border-red-400 bg-red-50 dark:bg-red-900/20 text-red-500"
              : "border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500"
          }`}
        >
          <span className="text-lg">&times;</span>
          <span className="type-button">Drop here to remove</span>
        </div>
      )}

      {/* Empty state */}
      {plan && plan.entries.length === 0 && !loading && (
        <p className="type-muted mt-8 text-center">
          No meals planned this week. Drag a recipe from below or click <span className="text-blue-500">+</span> to search.
        </p>
      )}

      {/* Browse Recipes */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="type-section-title">
            Recipes
            {browseTotal > 0 && <span className="type-muted ml-2 align-middle">({browseTotal})</span>}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setBrowsePage((p) => Math.max(1, p - 1))}
              disabled={!browseHasPrev}
              className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            >
              Prev
            </button>
            <span className="type-muted">
              Page {browsePage}{browseTotal > 0 && `/${Math.ceil(browseTotal / 12)}`}
            </span>
            <button
              onClick={() => setBrowsePage((p) => p + 1)}
              disabled={!browseHasNext}
              className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            >
              Next
            </button>
          </div>
        </div>

        {/* Source toggle + Filters */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => setBrowseMyRecipes(!browseMyRecipes)}
            className="flex items-center gap-1.5 text-sm font-medium transition"
          >
            <div className={`relative w-8 h-4.5 rounded-full transition-colors ${
              browseMyRecipes ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"
            }`} style={{ height: "18px" }}>
              <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${
                browseMyRecipes ? "translate-x-4" : "translate-x-0.5"
              }`} />
            </div>
            <span className={browseMyRecipes ? "text-blue-600" : "text-gray-500 dark:text-gray-400"}>
              {browseMyRecipes ? "My Recipes" : "All Recipes"}
            </span>
          </button>
          <div className="w-px h-5 bg-gray-300 dark:bg-gray-600" />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
          >
            <option value="">All Categories</option>
            {filterOptions.categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <select
            value={filterDiet}
            onChange={(e) => setFilterDiet(e.target.value)}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
          >
            <option value="">All Diets</option>
            {filterOptions.diets.map((diet) => (
              <option key={diet} value={diet}>{diet.charAt(0).toUpperCase() + diet.slice(1)}</option>
            ))}
          </select>
          <select
            value={filterTag}
            onChange={(e) => setFilterTag(e.target.value)}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
          >
            <option value="">All Tags</option>
            {filterOptions.popular_tags.map((tag) => (
              <option key={tag.id} value={tag.name}>#{tag.name}</option>
            ))}
          </select>
          {(filterCategory || filterDiet || filterTag) && (
            <button
              onClick={() => { setFilterCategory(""); setFilterDiet(""); setFilterTag(""); }}
              className="text-xs text-blue-500 hover:text-blue-600 transition"
            >
              Clear filters
            </button>
          )}
        </div>

        {browseLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {browseRecipes.map((recipe) => {
              const imageUrls = getRecipeImageUrls(recipe);
              const { periodMs, offsetMs } = getBrowseRotationConfig(recipe);
              const thumbnailUrl = imageUrls.length
                ? imageUrls[
                    Math.floor((browseImageClock + offsetMs) / periodMs) % imageUrls.length
                  ]
                : "";
              return (
              <div
                key={recipe.id}
                draggable
                onDragStart={(e) => handleBrowseDragStart(e, recipe)}
                onDragEnd={() => { setTimeout(() => { browseDraggedRef.current = false; }, 0); }}
                onClick={() => handleBrowseClick(recipe.id)}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/50 overflow-hidden cursor-pointer hover:shadow-lg transition group"
              >
                {thumbnailUrl ? (
                  <div className="w-full h-24 overflow-hidden bg-gray-100 dark:bg-gray-700">
                    <img
                      key={thumbnailUrl}
                      src={thumbnailUrl}
                      alt={recipe.title}
                      draggable="false"
                      className="planner-browse-card-image w-full h-24 object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-full h-24 bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-400 dark:text-gray-500 text-xs">
                    No image
                  </div>
                )}
                <div className="p-2">
                  <p className="type-card-title truncate">
                    {recipe.title}
                  </p>
                  <p className="type-muted mt-0.5 text-[10px] leading-4">
                    {recipe.category && recipe.category}
                    {recipe.prep_time && ` \u00b7 ${recipe.prep_time}m`}
                  </p>
                </div>
              </div>
            )})}
          </div>
        )}

        {!browseLoading && browseRecipes.length === 0 && (
          <p className="type-muted py-8 text-center">No recipes found</p>
        )}
      </div>

      {/* Recipe Picker Modal */}
      {pickerOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 pt-20">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[70vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <h3 className="type-section-title">
                Add Recipe — {DAY_NAMES[pickerTarget.day]}{" "}
                {pickerTarget.meal.charAt(0).toUpperCase() + pickerTarget.meal.slice(1)}
              </h3>
              <button
                onClick={closePicker}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl"
              >
                &times;
              </button>
            </div>

            {/* Search */}
            <div className="p-4 border-b dark:border-gray-700">
              <input
                type="text"
                placeholder="Search recipes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
                className="w-full border px-3 py-2 rounded-md text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              />
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto p-2">
              {(() => {
                const searching = searchQuery.trim().length > 0;
                const items = searching ? searchResults : pickerRanked;
                const isLoading = searching ? searchLoading : pickerRankedLoading;

                if (isLoading) {
                  return (
                    <p className="type-muted py-4 text-center">
                      {searching ? "Searching..." : "Loading recipes..."}
                    </p>
                  );
                }

                if (items.length === 0) {
                  return (
                    <p className="type-muted py-4 text-center">
                      {searching ? "No recipes found" : "No recipes yet — create one to get started"}
                    </p>
                  );
                }

                return (
                  <>
                    {!searching && (
                      <p className="type-eyebrow px-2 pb-2">
                        Trending &amp; Your Recipes
                      </p>
                    )}
                    {items.map((recipe) => {
                      const ownerName = recipe.owner
                        ? `${recipe.owner.first_name || ""} ${recipe.owner.last_name || ""}`.trim() ||
                          "Unknown"
                        : "Unknown";
                      const thumbnailUrl = getRecipeThumbnail(recipe);
                      const timeStr = [
                        recipe.prep_time ? `${recipe.prep_time}m prep` : null,
                        recipe.cook_time ? `${recipe.cook_time}m cook` : null,
                      ]
                        .filter(Boolean)
                        .join(" \u00b7 ");
                      return (
                        <button
                          key={recipe.id}
                          onClick={() =>
                            addEntry(recipe.id, pickerTarget.day, pickerTarget.meal)
                          }
                          className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition text-left"
                        >
                          {thumbnailUrl ? (
                            <img
                              src={thumbnailUrl}
                              alt=""
                              className="w-14 h-14 rounded-md object-cover shrink-0"
                            />
                          ) : (
                            <div className="w-14 h-14 rounded-md bg-gray-200 dark:bg-gray-600 shrink-0" />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="type-card-title truncate">
                              {recipe.title}
                            </p>
                            <p className="type-muted truncate text-xs leading-5">
                              by {ownerName}
                              {recipe.category ? ` \u00b7 ${recipe.category}` : ""}
                            </p>
                            {timeStr && (
                            <p className="type-muted truncate text-xs leading-4">
                              {timeStr}
                            </p>
                          )}
                          </div>
                          {recipe.likes_count > 0 && (
                            <span className="type-muted shrink-0 text-xs">
                              {"\u2665"} {recipe.likes_count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Clear week confirm */}
      {showClearConfirm && (
        <ConfirmModal
          title="Clear Week"
          message="Remove all recipes from this week's meal plan?"
          variant="danger"
          confirmText="Clear All"
          cancelText="Cancel"
          onConfirm={clearWeek}
          onCancel={() => setShowClearConfirm(false)}
        />
      )}

      {/* Sync to Cart result */}
      {cartResult && (
        <ConfirmModal
          title="Cart Synced"
          message={`${cartResult.added} added, ${cartResult.removed} removed. ${cartResult.total} total in cart.`}
          variant="success"
          confirmText="View Cart"
          cancelText="Close"
          onConfirm={() => {
            setCartResult(null);
            navigate("/cart");
          }}
          onCancel={() => setCartResult(null)}
        />
      )}
    </div>
  );
}
